import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { Check, Download, ArrowRight, RefreshCw } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 25; // ~50s max before giving up gracefully

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          track.cta("purchase_completed", "payment_success");
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("error");
          setError("We haven't received payment confirmation yet. If you completed checkout, please check your email — we'll send the download link the moment it clears.");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (attemptsRef.current >= 3) {
          setState("error");
          setError(err.response?.data?.detail || "Couldn't reach the server.");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  if (!sessionId) return <Navigate to="/checkout" replace />;

  return (
    <div data-testid="payment-success-page">
      <Seo
        title="Welcome to The Shift"
        description="Thank you for your order. Your copy of The Shift is ready."
        path="/payment/success"
      />
      <section className="relative bg-black noise pt-40 pb-32 lg:pt-52 lg:pb-40 min-h-[90vh] overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          {state === "polling" && <PollingView />}
          {state === "paid" && <PaidView sessionId={sessionId} />}
          {state === "expired" && <ExpiredView />}
          {state === "error" && <ErrorView message={error} />}
        </div>
      </section>
    </div>
  );
}

function PollingView() {
  return (
    <div data-testid="payment-polling">
      <div className="mx-auto w-16 h-16 border border-[#D4AF37]/40 rounded-full flex items-center justify-center">
        <RefreshCw size={22} className="text-[#D4AF37] animate-spin" strokeWidth={1.4} />
      </div>
      <p className="font-body text-xs tracking-[0.28em] uppercase text-[#D4AF37] mt-10">
        Confirming your order
      </p>
      <h1 className="font-display text-3xl sm:text-4xl text-white mt-4 leading-tight tracking-tight">
        Almost there…
      </h1>
      <p className="font-body text-white/55 mt-6 leading-relaxed">
        We&apos;re confirming your payment with Stripe. This usually takes a few seconds.
      </p>
    </div>
  );
}

function PaidView({ sessionId }) {
  return (
    <div data-testid="payment-paid">
      <Reveal>
        <div className="mx-auto w-20 h-20 lg:w-24 lg:h-24 border border-[#D4AF37] rounded-full flex items-center justify-center">
          <Check size={38} className="text-[#D4AF37]" strokeWidth={1.5} />
        </div>
      </Reveal>
      <Reveal delay={100}>
        <Eyebrow className="mt-10">Order Complete</Eyebrow>
      </Reveal>
      <Reveal delay={160}>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-[1.06] tracking-tight">
          Welcome to <span className="text-[#D4AF37] italic">The Shift.</span>
        </h1>
      </Reveal>
      <Reveal delay={240}>
        <p className="font-body text-lg lg:text-xl text-white/60 font-light mt-8 leading-relaxed">
          Your copy just landed in your inbox. If you don&apos;t see it within a few minutes,
          check Promotions or Spam and mark it as Primary so future emails from me don&apos;t
          get lost.
        </p>
      </Reveal>
      <Reveal delay={320}>
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/the-shift?session_id=${encodeURIComponent(sessionId)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="payment-download-link"
            className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium text-[15px] px-9 py-4 transition-all duration-300"
          >
            <Download size={16} /> Download Your Copy
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-[#D4AF37] font-body font-medium border-b border-white/25 hover:border-[#D4AF37] pb-1 transition-colors"
          >
            Return home <ArrowRight size={14} />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={420}>
        <p className="font-display italic text-lg text-white/70 mt-16">Keep Building.</p>
        <p className="font-body text-xs tracking-[0.28em] uppercase text-white/50 mt-2">
          — Don Orr Jr.
        </p>
      </Reveal>
    </div>
  );
}

function ExpiredView() {
  return (
    <div data-testid="payment-expired">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-white/50">Session expired</p>
      <h1 className="font-display text-3xl sm:text-4xl text-white mt-4 leading-tight tracking-tight">
        This checkout session has expired.
      </h1>
      <p className="font-body text-white/55 mt-6 leading-relaxed">
        No worries — you can start again in one click.
      </p>
      <Link
        to="/checkout"
        className="mt-8 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium px-8 py-4"
      >
        Try again <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function ErrorView({ message }) {
  return (
    <div data-testid="payment-error">
      <p className="font-body text-xs tracking-[0.28em] uppercase text-white/50">Still processing</p>
      <h1 className="font-display text-3xl sm:text-4xl text-white mt-4 leading-tight tracking-tight">
        Give it another minute.
      </h1>
      <p className="font-body text-white/55 mt-6 leading-relaxed max-w-md mx-auto">{message}</p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center gap-2 text-white/70 hover:text-[#D4AF37] font-body font-medium border-b border-white/25 hover:border-[#D4AF37] pb-1 transition-colors"
      >
        Return home <ArrowRight size={14} />
      </Link>
    </div>
  );
}
